import { SETTINGS_BACKGROUND_KEY, SETTINGS_INDENT_KEY, SETTINGS_POSITIONING_KEY, SETTINGS_SCHEME_KEY, SETTINGS_SIDEBAR_KEY, SETTINGS_THEME_KEY, SETTINGS_VERBOSE_KEY } from "../lib/constants.js";
import { clearTokenCache } from "../lib/tokens.js";
import { enumerateGroups } from "../lib/utils.js";
import { cachedRef, computedModel } from "../lib/utils_vue.js";
import { buildDependencyMap, shouldHideProp } from "../lib/prop_dependencies.js";
import { shouldExclude } from "../lib/exclusion_rules.js";
import _sfc_main$1 from "./option_bar/option_bar.vue.js";
import _sfc_main$2 from "./renderer/renderer.vue.js";
import { getComponentInfo } from "../lib/info.js";
import settings_default from "../settings.json.js";
import _sfc_main$3 from "./code_panel/code_panel.vue.js";
/* empty css                                                */
import { Fragment, computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, createTextVNode, createVNode, isRef, mergeProps, nextTick, normalizeClass, normalizeStyle, onErrorCaptured, openBlock, reactive, ref, renderList, resolveComponent, resolveDirective, toDisplayString, unref, watch, withCtx, withDirectives } from "vue";
import DtIconMinimize from "@dialpad/dialtone-icons/vue/minimize";
import DtIconExpand from "@dialpad/dialtone-icons/vue/expand";
import DtIconRefresh from "@dialpad/dialtone-icons/vue/refresh";
import DtIconChevronsUpDown from "@dialpad/dialtone-icons/vue/chevrons-up-down";
import DtIconCheck from "@dialpad/dialtone-icons/vue/check";
//#region src/components/combinator.vue
var _hoisted_1 = { class: "dialtone-playground__start" };
var _hoisted_2 = { class: "dialtone-playground__end" };
var _sfc_main = /* @__PURE__ */ Object.assign({ name: "DtcCombinator" }, {
	props: {
		component: {
			type: Object,
			required: true
		},
		documentation: {
			type: Object,
			required: true
		},
		library: {
			type: Object,
			default: () => ({})
		},
		variants: {
			type: Object,
			default: () => ({})
		},
		blueprint: {
			type: Boolean,
			default: false
		},
		devMode: {
			type: Boolean,
			default: false
		}
	},
	setup(__props) {
		const props = __props;
		const selectedVariant = ref("default");
		const activeVariant = ref("default");
		const isFullScreen = ref(false);
		const optionBarWidth = ref(null);
		let _presetChanging = false;
		const _forceReset = ref(0);
		const variantOptions = computed(() => {
			return Object.keys(props.variants ?? {}).filter((key) => key !== "exclusions" && key !== "defaults").map((key) => ({
				value: key,
				label: key
			}));
		});
		/**
		* Container for all extended component information for the target component.
		* Immutable.
		*
		* @type {ComputedRef<object>}
		*/
		const info = computed(() => {
			return Object.freeze({
				...initializeInfo(),
				members: { enumerate(handler) {
					enumerateGroups(handler, {
						slots: info.value.slots,
						props: info.value.props,
						attributes: info.value.attributes,
						events: info.value.events
					});
				} },
				bindings: {
					get() {
						const bindings = [];
						this.enumerate((_, binding) => bindings.push(binding));
						return bindings;
					},
					enumerate(handler) {
						enumerateGroups(handler, {
							props: info.value.props,
							attributes: info.value.attributes?.filter((attribute) => attribute)
						});
					}
				}
			});
		});
		/**
		* The options data object is the main reactive object that allows interactivity with the target component.
		* Reactive.
		*
		* @type {WritableComputedRef<object>}
		*/
		const options = computedModel(
			computed(() => {
				_forceReset.value;
				return reactive({
					...getInitialValues(info.value),
					bindings: {
						get() {
							const bindings = [];
							this.enumerate((_, binding) => bindings.push(binding));
							return Object.fromEntries(bindings);
						},
						enumerate(handler) {
							enumerateGroups(handler, {
								props: options.value.props ? Object.entries(options.value.props) : null,
								attributes: options.value.attributes ? Object.entries(options.value.attributes) : null
							});
						}
					}
				});
			}),
			/**
			* Catch errors when updating member values,
			* else vue can block the value from ever being changed,
			* keeping the value permanently in an invalid state
			*
			* @param e
			* @param model
			*/
			(e, model) => {
				try {
					e(model);
					if (!_presetChanging) selectedVariant.value = "";
				} catch (exception) {
					console.warn("Update options warning: \n", exception);
				}
			}
		);
		/**
		* The settings data object used to read and write cached settings used throughout the combinator.
		*
		* @type {WritableComputedRef<object>}
		*/
		const settings = computedModel(computed(() => {
			return reactive({
				root: {
					theme: cachedRef(SETTINGS_THEME_KEY, settings_default.root["default-theme"]),
					sidebar: cachedRef(SETTINGS_SIDEBAR_KEY, settings_default.root["default-sidebar"])
				},
				renderer: {
					positioning: cachedRef(SETTINGS_POSITIONING_KEY, settings_default.renderer["default-positioning"]),
					background: cachedRef(SETTINGS_BACKGROUND_KEY, settings_default.renderer["default-background"])
				},
				code: {
					scheme: cachedRef(SETTINGS_SCHEME_KEY, settings_default.code["default-scheme"]),
					indent: cachedRef(SETTINGS_INDENT_KEY, settings_default.code["default-indent-spaces"]),
					verbose: props.blueprint ? false : cachedRef(SETTINGS_VERBOSE_KEY, settings_default.code["default-verbose"])
				}
			});
		}), (e, model) => {
			try {
				e(model);
			} catch (exception) {
				console.warn("Update settings warning: \n", exception);
			}
		});
		watch(() => settings.value.root.theme, clearTokenCache);
		function updateVariant(e) {
			_presetChanging = true;
			selectedVariant.value = e;
			if (e !== "") {
				activeVariant.value = e;
				_forceReset.value++;
			}
			nextTick(() => {
				_presetChanging = false;
			});
		}
		/**
		* Merges variant override data into an info object.
		*
		* @param {object} info - The info object to merge into.
		* @param {object} variantData - The variant data to merge.
		*/
		function mergeVariantData(info, variantData) {
			if (!variantData) return;
			Object.entries(variantData).forEach(([memberGroup, members]) => {
				if (memberGroup === "exclusions") return;
				Object.entries(members).forEach(([memberName, member]) => {
					const infoMember = info[memberGroup]?.find((m) => m.name === memberName);
					if (infoMember) Object.assign(infoMember, member);
				});
			});
		}
		const defaultInfo = computed(() => {
			const info = cloneInfoMembers(getComponentInfo(props.component, props.documentation));
			mergeVariantData(info, props.variants?.defaults);
			mergeVariantData(info, props.variants?.default);
			return info;
		});
		/**
		* Shallow-clones member arrays and their objects so that variant overrides
		* never mutate the shared documentation prop.
		*
		* @param {object} info - The info object to clone.
		* @returns {object} A cloned info object.
		*/
		function cloneInfoMembers(info) {
			const cloned = { ...info };
			for (const group of [
				"props",
				"slots",
				"attributes",
				"events"
			]) if (cloned[group]) cloned[group] = cloned[group].map((m) => ({ ...m }));
			return cloned;
		}
		/**
		* Gets a new instantiation of an info object.
		* Merges info from selected variant to the info object.
		*
		* @returns {object} The newly instantiated info object.
		*/
		function initializeInfo() {
			const info = cloneInfoMembers(getComponentInfo(props.component, props.documentation));
			mergeVariantData(info, props.variants?.defaults);
			mergeVariantData(info, props.variants?.[activeVariant.value]);
			info.exclusions = props.variants?.exclusions ?? [];
			return info;
		}
		/**
		* Gets the values for a given 'options' member group with the provided defaults.
		*
		* @param info
		*/
		function getInitialValues(info) {
			const options = {};
			info.members.enumerate((memberGroup, member) => {
				options[memberGroup] = options[memberGroup] || {};
				options[memberGroup][member.name] = member.initialValue;
			});
			return options;
		}
		const hasChanges = computed(() => {
			const referenceInfo = defaultInfo.value ?? info.value;
			for (const group of [
				"props",
				"slots",
				"attributes"
			]) {
				const members = referenceInfo[group];
				if (!members) continue;
				for (const member of members) if (options.value[group]?.[member.name] !== member.initialValue) return true;
			}
			return false;
		});
		function resetOptions() {
			updateVariant("default");
		}
		function startResize(e) {
			e.preventDefault();
			const startX = e.clientX;
			const startWidth = document.querySelector(".dialtone-playground__controls")?.offsetWidth ?? 0;
			function onMove(e) {
				const delta = startX - e.clientX;
				optionBarWidth.value = Math.max(200, startWidth + delta) + "px";
			}
			function onUp() {
				document.removeEventListener("pointermove", onMove);
				document.removeEventListener("pointerup", onUp);
			}
			document.addEventListener("pointermove", onMove);
			document.addEventListener("pointerup", onUp);
		}
		function toggleFullScreen() {
			isFullScreen.value = !isFullScreen.value;
			if (isFullScreen.value) document.body.classList.add("d-of-hidden", "d-h100vh");
			else document.body.classList.remove("d-of-hidden", "d-h100vh");
		}
		/**
		* Set of member names that are currently disabled via exclusion rules or prop dependencies.
		* Passed to the code panel so disabled members are suppressed from the code snippet.
		*
		* @type {ComputedRef<Set<string>>}
		*/
		const disabledMembers = computed(() => {
			const disabled = /* @__PURE__ */ new Set();
			const exclusions = info.value.exclusions;
			const propValues = options.value.props;
			const depMap = buildDependencyMap(info.value.props);
			for (const member of info.value.props) {
				if (member.required) continue;
				if (shouldExclude(member.name, "props", exclusions, propValues) || shouldHideProp(member.name, depMap, propValues)) disabled.add(member.name);
			}
			for (const member of info.value.slots ?? []) {
				if (member.required) continue;
				if (shouldExclude(member.name, "slots", exclusions, propValues)) disabled.add(member.name);
			}
			return disabled;
		});
		onErrorCaptured((exception) => {
			console.error("Internal vue error: \n", exception);
			return false;
		});
		return (_ctx, _cache) => {
			const _component_dt_text = resolveComponent("dt-text");
			const _component_dt_button = resolveComponent("dt-button");
			const _component_dt_list_item = resolveComponent("dt-list-item");
			const _component_dt_dropdown = resolveComponent("dt-dropdown");
			const _component_dt_stack = resolveComponent("dt-stack");
			const _directive_dt_tooltip = resolveDirective("dt-tooltip");
			return openBlock(), createElementBlock("div", { class: normalizeClass(["dialtone-playground", { "dialtone-playground--fullscreen": isFullScreen.value }]) }, [
				createVNode(_component_dt_stack, {
					direction: "row",
					gap: "500",
					justify: "between",
					class: normalizeClass(["d-p-100 d-pie-200 d-bb d-bc-subtle", variantOptions.value.length > 1 ? "d-g-cols3" : "d-g-cols2"])
				}, {
					default: withCtx(() => [variantOptions.value.length < 2 ? (openBlock(), createBlock(_component_dt_text, {
						key: 0,
						kind: "code",
						tone: "primary",
						strength: "semibold",
						size: 300,
						as: "div",
						class: "d-px-150 d-py-100"
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(__props.component.name), 1)]),
						_: 1
					})) : (openBlock(), createBlock(_component_dt_dropdown, {
						key: 1,
						"navigation-type": "arrow-keys",
						placement: "bottom-start",
						"content-class": "d-wmn-500"
					}, {
						anchor: withCtx(({ attrs }) => [withDirectives((openBlock(), createBlock(_component_dt_button, mergeProps(attrs, {
							importance: "clear",
							kind: "muted",
							size: isFullScreen.value ? "lg" : "md",
							"leading-class": "d-pbs-1 d-pis-150 d-mie-n25"
						}), {
							leading: withCtx(() => [createVNode(_component_dt_text, {
								kind: "code",
								tone: "primary",
								strength: "semibold",
								class: "d-fs-inherit"
							}, {
								default: withCtx(() => [createTextVNode(toDisplayString(__props.component.name) + ": ", 1)]),
								_: 1
							})]),
							endIcon: withCtx(({ iconSize }) => [createVNode(unref(DtIconChevronsUpDown), {
								class: "d-fc-muted",
								size: iconSize
							}, null, 8, ["size"])]),
							default: withCtx(() => [createTextVNode(" " + toDisplayString(selectedVariant.value || "custom") + " ", 1)]),
							_: 1
						}, 16, ["size"])), [[_directive_dt_tooltip, "Presets"]])]),
						list: withCtx(({ close }) => [(openBlock(true), createElementBlock(Fragment, null, renderList(variantOptions.value, (option) => {
							return openBlock(), createBlock(_component_dt_list_item, {
								key: option.value,
								role: "menuitem",
								"navigation-type": "arrow-keys",
								onClick: ($event) => {
									updateVariant(option.value);
									close();
								}
							}, {
								end: withCtx(() => [createVNode(unref(DtIconCheck), {
									size: "200",
									class: normalizeClass(option.value === selectedVariant.value ? "d-o100" : "d-o0")
								}, null, 8, ["class"])]),
								default: withCtx(() => [createTextVNode(toDisplayString(option.label) + " ", 1)]),
								_: 2
							}, 1032, ["onClick"]);
						}), 128))]),
						_: 1
					})), createVNode(_component_dt_stack, {
						gap: "100",
						direction: "row"
					}, {
						default: withCtx(() => [hasChanges.value ? withDirectives((openBlock(), createBlock(_component_dt_button, {
							key: 0,
							kind: "muted",
							importance: "clear",
							size: 200,
							onClick: resetOptions
						}, {
							icon: withCtx(({ iconSize }) => [createVNode(unref(DtIconRefresh), { size: iconSize }, null, 8, ["size"])]),
							_: 1
						})), [[_directive_dt_tooltip, `Reset`]]) : createCommentVNode("", true), withDirectives((openBlock(), createBlock(_component_dt_button, {
							kind: "muted",
							importance: "clear",
							size: 200,
							onClick: toggleFullScreen
						}, {
							icon: withCtx(({ iconSize }) => [isFullScreen.value ? (openBlock(), createBlock(unref(DtIconMinimize), {
								key: 0,
								size: iconSize
							}, null, 8, ["size"])) : (openBlock(), createBlock(unref(DtIconExpand), {
								key: 1,
								size: iconSize
							}, null, 8, ["size"]))]),
							_: 1
						})), [[_directive_dt_tooltip, `Fullscreen`]])]),
						_: 1
					})]),
					_: 1
				}, 8, ["class"]),
				createElementVNode("div", _hoisted_1, [
					createVNode(_sfc_main$2, {
						settings: unref(settings),
						"onUpdate:settings": _cache[0] || (_cache[0] = ($event) => isRef(settings) ? settings.value = $event : null),
						class: "dialtone-playground__component",
						component: __props.component,
						info: info.value,
						options: unref(options),
						library: __props.library,
						"disabled-members": disabledMembers.value
					}, null, 8, [
						"settings",
						"component",
						"info",
						"options",
						"library",
						"disabled-members"
					]),
					createElementVNode("div", {
						class: "dialtone-playground__resizer",
						onPointerdown: startResize,
						onDblclick: _cache[1] || (_cache[1] = ($event) => optionBarWidth.value = null)
					}, null, 32),
					!__props.blueprint ? (openBlock(), createBlock(_sfc_main$1, {
						key: 0,
						options: unref(options),
						"onUpdate:options": _cache[2] || (_cache[2] = ($event) => isRef(options) ? options.value = $event : null),
						component: __props.component,
						info: info.value,
						style: normalizeStyle(optionBarWidth.value ? { "inline-size": optionBarWidth.value } : {})
					}, null, 8, [
						"options",
						"component",
						"info",
						"style"
					])) : createCommentVNode("", true)
				]),
				createElementVNode("div", _hoisted_2, [createVNode(_sfc_main$3, {
					info: info.value,
					options: unref(options),
					settings: unref(settings),
					"disabled-members": disabledMembers.value,
					"dev-mode": __props.devMode,
					"has-changes": hasChanges.value,
					"full-screen": isFullScreen.value,
					"onUpdate:options": _cache[3] || (_cache[3] = (e) => e(unref(options)))
				}, null, 8, [
					"info",
					"options",
					"settings",
					"disabled-members",
					"dev-mode",
					"has-changes",
					"full-screen"
				])])
			], 2);
		};
	}
});
//#endregion
export { _sfc_main as default };

//# sourceMappingURL=combinator.vue.js.map